import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  Box,
  Grid,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import {
  getDocumentPreSignedURLForVendor,
  uploadDocument,
} from "src/app-redux/work-order/actions/workorderActions";
import { IDocumentResponse } from "src/common/typings/s3-file-metadata";
import AlertDialog from "src/components/AlertDialog/AlertDialog";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonButtonOutLined from "src/components/CommonButton/CommonButtonOutLined";
import CommonDrawer from "src/components/CommonDrawer/CommonDrawer";
import CommonTextField from "src/components/CommonTextField/CommonTextField";
import NoRecordFound from "src/components/NoRecordFound";
import { Card } from "./Card";
import Heading from "./Heading";
import { useStyles } from "./styles";

type Props = {
  expand: boolean;
  updateExpandStatus: (type: number) => void;
};

export function Doc({ expand, updateExpandStatus }: Props): React.ReactElement {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [documentTypes, setDocumentTypes] = React.useState<any[]>();
  const [fileToUpload, setFileToUpload] = React.useState<File>();
  const [loader, setLoader] = useState(false);
  const [docData, setDocData] = useState({});
  const [docType, setDocType] = useState<string>("");
  const [docId, setDocId] = useState<string>("");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isOpenDilog, setIsOpenDilog] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");
  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFileToUpload(acceptedFiles[0]);
  }, []);
  const getDocData = async () => {
    try {
      setLoader(true);
      const { documents } = await fetchWrapper.get("get-vendor-documents-by-id-by-vendor");
      setDocData(documents);
      return setLoader(false);
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
  };
  const addDoc = async () => {
    setLoader(true);
    if (fileToUpload !== undefined) {
      const document: any = {
        FileName: fileToUpload?.name,
        Type: docType,
        ContentType: fileToUpload?.type,
        FileExtension: fileToUpload?.name.split(".")[1],
        VendNum: 0,
        Comment: note || "",
      };
      const res: any = await dispatch(getDocumentPreSignedURLForVendor(document));
      if (res?.payload) {
        const payload = res.payload as IDocumentResponse;
        const res1: any = await dispatch(
          uploadDocument({
            url: payload.url,
            formData: fileToUpload,
            contentType: document.ContentType,
            size: fileToUpload.size,
          })
        );
        if (res1?.payload) {
          getDocData();
          setIsOpenDilog(false);
        }
      }
    } else {
      dispatch(
        addNotificationAction({
          message: "Please select a file to upload",
          options: { variant: "error" },
        })
      );
    }

    setLoader(false);
  };
  const deleteDoc = async (id: string) => {
    setDocId(id);
    setIsConfirmDialogOpen(true);
  };
  const deleteDocuments = async () => {
    setLoader(true);
    try {
      setLoader(true);
      const res = await fetchWrapper.get(`delete-document-by-id/${parseInt(docId)}`);
      if (res.success) {
        dispatch(
          addNotificationAction({
            message: "Document deleted successfully!",
            options: { variant: "success" },
          })
        );
        getDocData();
      } else {
        throw res.error;
      }
    } catch (error: any) {
      setLoader(false);
      dispatch(
        addNotificationAction({
          message: error.message,
          options: { variant: "error" },
        })
      );
    }
    setIsConfirmDialogOpen(false);
    setLoader(false);
  };
  async function getDocumentTypes() {
    setLoader(true);
    if (!documentTypes?.length) {
      const { documentTypes } = await fetchWrapper.get("/get-document-type/vendor");
      if (documentTypes) {
        const res: any[] = [];
        if (documentTypes?.length > 0)
          documentTypes.map((v: any) => {
            const obj: any = {
              label: v.code,
              value: v.code,
            };
            res.push(obj);
          });
        setDocumentTypes(res);
        setLoader(false);
      }
    }
    setLoader(false);
  }
  useEffect(() => {
    if (expand) {
      getDocData();
    }
  }, [expand]);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  return (
    <>
      <Backdrop className={classes.backdrop} open={loader}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box border={1} mb={2} borderColor="grey.200">
        <Accordion square elevation={0} expanded={expand} onChange={() => updateExpandStatus(4)}>
          <Heading heading="Documents" />
          <Box borderTop={1} mb={1} borderColor="grey.200"></Box>
          <AccordionDetails>
            <Grid container direction="row">
              <Grid xs={12} md={12}>
                <Box mt={2} />
                {Boolean(Object.keys(docData).length) ? (
                  Object.values(docData).map((item, i) => (
                    <Card key={i} index={i} RowData={item} deleteDoc={deleteDoc} />
                  ))
                ) : (
                  <>
                    <NoRecordFound />
                  </>
                )}
                <Grid item xs={12}>
                  <Box component="span">
                    <CommonButtonOutLined
                      isGreen
                      onClick={() => {
                        setIsOpenDilog(true);
                        setFileToUpload(undefined);
                        setDocType("");
                        getDocumentTypes();
                        setNote("");
                      }}
                    >
                      Upload New Document
                    </CommonButtonOutLined>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Box>
      <Grid container direction="row">
        <CommonDrawer
          heading={"Documents Information"}
          isOpen={isOpenDilog}
          closeDrawer={() => {
            setIsOpenDilog(false);
          }}
          drawerWidth={500}
        >
          <Box mt={2} />
          <Grid item xs={12} md={12}>
            <Box mr={2}>
              <CommonAutoCompleteField
                label="Type"
                name="type"
                optionsType={documentTypes}
                required
                getOptionLabel={(option: any) => option.label || ""}
                value={documentTypes?.find((ty: any) => ty.value === docType) || null}
                onChange={(option: any) => setDocType(option?.value || "")}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={12}>
            <Box
              {...getRootProps()}
              p={0}
              pl={2}
              mr={2}
              mt={3}
              border={"1px solid #72CA00"}
              borderRadius={5}
              height={38}
              pb={4}
              borderColor={isDragActive ? "error.main" : "secondary.main"}
            >
              <input {...getInputProps()} />

              {fileToUpload ? (
                <p>File Name: {fileToUpload.name}</p>
              ) : isDragActive ? (
                <p>Drop a file here ...</p>
              ) : (
                <p style={{ color: "rgb(181 178 178)", marginTop: 10, paddingBottom: 10 }}>
                  Drag and drop file here, or click to select a file
                </p>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={12}>
            <Box my={2} mr={2}>
              <CommonTextField
                // required
                label="Comment"
                onChange={(v: any) => {
                  setNote(v.target.value);
                }}
                name="Comment"
                value={note}
                rows={2}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mt={2} mb={2} textAlign="right">
              <Box component="span" mr={2}>
                <CommonButton
                  onClick={() => {
                    setIsOpenDilog(false);
                  }}
                >
                  Cancel
                </CommonButton>
              </Box>
              <Box component="span">
                <CommonButton
                  isGreen
                  disable={!(docType && fileToUpload)}
                  onClick={() => {
                    addDoc();
                  }}
                >
                  Save
                </CommonButton>
              </Box>
            </Box>
          </Grid>
        </CommonDrawer>
      </Grid>
      <AlertDialog
        isOpen={isConfirmDialogOpen}
        handleClose={() => {
          setIsConfirmDialogOpen(false);
        }}
        message={"Are you sure you want to Delete this document?"}
        primaryButtonText="Ok"
        onPrimaryButtonClick={deleteDocuments}
        heading={"Delete Documents"}
      />
    </>
  );
}
